import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { UserPreferencesService } from '~shared/services/preferences/user-preferences.service';

/** Thèmes d'accessibilité. `default` = aucun attribut `data-theme` sur `:root`. */
export type A11yTheme = 'default' | 'high-contrast';

/** Clé de la préférence utilisateur (persistée par UserPreferencesService). */
export const A11Y_THEME_PREFERENCE_KEY = 'a11yTheme';

/**
 * Applique un thème d'accessibilité en posant `data-theme` sur `:root`, ce qui
 * surcharge les custom properties de couleur (cf. `src/styles/themes/`). Les
 * composants n'ont rien à changer : ils lisent déjà `var(--token, …)`.
 *
 * Le choix est persisté PAR UTILISATEUR (localStorage + API, via
 * UserPreferencesService) et réappliqué au démarrage par le APP_INITIALIZER —
 * même mécanisme que la langue (cf. LocaleService).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly preferences = inject(UserPreferencesService);
  private readonly _theme = signal<A11yTheme>('default');

  /** Thème courant (lecture seule, pour refléter l'état dans l'UI). */
  readonly theme = this._theme.asReadonly();

  /**
   * Réapplique le thème stocké. Synchrone (lecture localStorage) pour poser
   * `data-theme` avant le premier rendu, sans clignotement.
   */
  init(): void {
    const stored = this.preferences.read<A11yTheme>(A11Y_THEME_PREFERENCE_KEY);
    if (stored === 'high-contrast' || stored === 'default') {
      // Choix EXPLICITE : il prime sur la préférence système.
      this.apply(stored);
      return;
    }
    // Aucun choix enregistré : le système décide (via `@media prefers-contrast`,
    // cf. styles/themes/_high-contrast). On ne pose PAS `data-theme` — le média
    // gouverne — mais le signal reflète l'état EFFECTIF pour que l'interrupteur
    // reste cohérent avec ce qui est affiché.
    this._theme.set(this.osPrefersHighContrast() ? 'high-contrast' : 'default');
  }

  /** Le système demande-t-il plus de contraste (`prefers-contrast: more`) ? */
  private osPrefersHighContrast(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-contrast: more)').matches
    );
  }

  /**
   * Change le thème : l'applique TOUT DE SUITE (retour visuel immédiat), puis le
   * persiste. L'Observable propage l'échec de l'API pour que l'UI remette
   * l'interrupteur sur l'état réellement enregistré — `UserPreferencesService`
   * restaure alors le localStorage.
   */
  setTheme(theme: A11yTheme): Observable<unknown> {
    this.apply(theme);
    return this.preferences.write(A11Y_THEME_PREFERENCE_KEY, theme);
  }

  /**
   * Applique un thème SANS le persister (retour visuel seul). Sert à revenir en
   * arrière quand `setTheme` échoue à enregistrer : `UserPreferencesService` a
   * déjà restauré le localStorage, il ne reste qu'à remettre l'écran dessus.
   */
  apply(theme: A11yTheme): void {
    this._theme.set(theme);
    // `data-theme` est TOUJOURS posé, y compris pour `default` : un choix
    // explicite doit primer sur `@media (prefers-contrast: more)` — sans quoi un
    // utilisateur qui désactive le contraste resterait en haut contraste imposé
    // par le système.
    document.documentElement.setAttribute('data-theme', theme);
  }
}
